import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  BodySchemaActions,
  BodySchemaActionTypes
} from './body-schema.actions';
import {
  BodySchemaEditingMode,
  BodySchemaFace,
  BodySchemaGeneralPain,
  BodySchemaMouthPainLocation,
  BodySchemaPelvicPainLocation,
  BodySchemaZone
} from './body-schema.models';

const MOUTH_ZONE_IDX = 35;
const PELVIC_FRONT_ZONE_IDX = [
  283, 284, 285, 286, 287, 294, 295, 296, 297, 298
];
const PELVIC_BACK_ZONE_IDX = [282, 291, 292];

interface BodySchemaState {
  color: string;
  useSurfacePain: boolean;
  zoom: number;
  face: BodySchemaFace;
  editingMode: BodySchemaEditingMode;
  painZonesBack: Array<BodySchemaZone>;
  painZonesFront: Array<BodySchemaZone>;
  generalPain: Array<BodySchemaGeneralPain>;
  mouthPainLocation: Array<BodySchemaMouthPainLocation>;
  pelvicPainLocation: Array<BodySchemaPelvicPainLocation>;
}

export const bodySchemaInitialState: BodySchemaState = {
  color: undefined,
  useSurfacePain: false,
  zoom: 1,
  face: 'FRONT',
  editingMode: undefined,
  painZonesBack: [],
  painZonesFront: [],
  generalPain: [],
  mouthPainLocation: undefined,
  pelvicPainLocation: undefined
};

export function bodySchemaReducer(
  state = bodySchemaInitialState,
  action: BodySchemaActions
): BodySchemaState {
  switch (action.type) {
    case BodySchemaActionTypes.LOAD_BODY_SCHEMA:
    case BodySchemaActionTypes.RESET_BODY_SCHEMA:
    case BodySchemaActionTypes.SAVE_BODY_SCHEMA_SUCCESS:
      return bodySchemaInitialState;

    case BodySchemaActionTypes.LOAD_BODY_SCHEMA_SUCCESS:
      return {
        ...state,
        ...action.payload
      };

    case BodySchemaActionTypes.UPDATE_ZOOM:
      const zoom = state.zoom + action.payload;
      return zoom > 0 && zoom < 4 ? { ...state, zoom: zoom } : state;

    case BodySchemaActionTypes.SET_EDITING_MODE:
      return { ...state, editingMode: action.payload };

    case BodySchemaActionTypes.SET_GENERAL_PAIN:
      return { ...state, generalPain: action.payload };

    case BodySchemaActionTypes.SET_MOUTH_PAIN_LOCATION:
      return { ...state, mouthPainLocation: action.payload };

    case BodySchemaActionTypes.SET_PELVIC_PAIN_LOCATION:
      return { ...state, pelvicPainLocation: action.payload };

    case BodySchemaActionTypes.SWITCH_FACE:
      return { ...state, face: state.face === 'FRONT' ? 'BACK' : 'FRONT' };

    case BodySchemaActionTypes.EDIT_ZONE:
      const zones = (
        action.payload.isBack ? state.painZonesBack : state.painZonesFront
      ).filter(z => z.idx !== action.payload.idx);

      if (state.editingMode !== 'DELETE') {
        zones.push({ idx: action.payload.idx, value: state.editingMode });
      }

      return state.editingMode
        ? action.payload.isBack
          ? { ...state, painZonesBack: zones }
          : { ...state, painZonesFront: zones }
        : state;

    default:
      return state;
  }
}

const bodySchemaState = createFeatureSelector<BodySchemaState>('bodySchema');

export const selectorBodySchemaColor = createSelector(
  bodySchemaState,
  state => state.color
);

export const selectorBodySchemaUseSurfacePain = createSelector(
  bodySchemaState,
  state => state.useSurfacePain
);

export const selectorBodySchemaZoom = createSelector(
  bodySchemaState,
  state => state.zoom
);

export const selectorBodySchemaEditingMode = createSelector(
  bodySchemaState,
  state => state.editingMode
);

export const selectorBodySchemaPainZonesBack = createSelector(
  bodySchemaState,
  state => state.painZonesBack
);

export const selectorBodySchemaPainZonesFront = createSelector(
  bodySchemaState,
  state => state.painZonesFront
);

export const selectorBodySchemaZonesSelected = createSelector(
  bodySchemaState,
  state => !!state.painZonesBack.length || !!state.painZonesFront.length
);

export const selectorBodySchemaMouthZoneSelected = createSelector(
  bodySchemaState,
  state => !!state.painZonesFront.find(zone => zone.idx === MOUTH_ZONE_IDX)
);

export const selectorBodySchemaPelvicZoneSelected = createSelector(
  bodySchemaState,
  state =>
    !!state.painZonesFront.find(zone =>
      PELVIC_FRONT_ZONE_IDX.includes(zone.idx)
    ) ||
    !!state.painZonesBack.find(zone => PELVIC_BACK_ZONE_IDX.includes(zone.idx))
);

export const selectorBodySchemaFace = createSelector(
  bodySchemaState,
  state => state.face
);

export const selectorBodySchemaDataForMOBI = createSelector(
  bodySchemaState,
  state => {
    const data = {
      pain_zones_front: state.painZonesFront.map(zone => ({
        id: `zone_${zone.idx}`,
        type: zone.value.type,
        value: zone.value.weight
      })),
      pain_zones_back: state.painZonesBack.map(zone => ({
        id: `zone_${zone.idx}`,
        type: zone.value.type,
        value: zone.value.weight
      })),
      general_pain: state.generalPain
    };

    if (
      selectorBodySchemaMouthZoneSelected &&
      selectorBodySchemaPelvicZoneSelected
    ) {
      return {
        ...data,
        mouth_pain_location: state.mouthPainLocation,
        pelvic_pain_location: state.pelvicPainLocation
      };
    } else if (
      selectorBodySchemaMouthZoneSelected ||
      selectorBodySchemaPelvicZoneSelected
    ) {
      return selectorBodySchemaMouthZoneSelected
        ? { ...data, mouth_pain_location: state.mouthPainLocation }
        : { ...data, pelvic_pain_location: state.pelvicPainLocation };
    } else {
      return data;
    }
  }
);
